import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "./TailwindClassnames";

/**
 * What a page shows in place of its data when its primary request answers **403** — the
 * session is alive, this content is refused (AKL-939). A heading, one sentence, and a real
 * link back to the projects list, rendered where the page's content would be so the side
 * menu and header stay in place and the app does not look broken.
 *
 * It does nothing else on purpose: no toast, no sign-out, no storage write. A 403 used to
 * clear the stored token and sign the user out on their next click; the interceptor now hands
 * a 403 back as an ordinary normalized 4xx (`isForbiddenError` in `apiUtils`), and the page
 * decides to render this. The link goes to `/` because the projects list is the one page every
 * signed-in user may see — a "back" to a page the user may also not see would be worse.
 */
const NoAccess: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="mt-8 text-left" data-testid="no-access">
      <h1 className={tailwindClassnames.pageTitle}>
        {t("You do not have access to this page")}
      </h1>
      <p className={`${tailwindClassnames.helperText} mt-2`}>
        {t(
          "Your account does not have permission to view it. If you think it should, ask your administrator.",
        )}
      </p>
      <Link
        to="/"
        className={`${tailwindClassnames.btnSecondary} mt-6 inline-block`}
        data-testid="no-access-back-to-projects"
      >
        {t("Back to projects")}
      </Link>
    </div>
  );
};

export default NoAccess;
